import type { HoverIdlePaymentState } from "@/features/payment/types";
import { HOV_SKILL_HOLD_MS } from "@/features/hoverIdle/hov/hovSkillDefinitions";
import { DEFAULT_WALL_START_CHARGE_MS } from "@/features/hoverIdle/wall/wallStartCharge";
import { detectBrowserLocale } from "@/i18n/runtime";
import { hugeZero } from "@/shared/numbers";
import type {
  HoverIdleConfig,
  HoverIdleCumulativeStatsState,
  HoverIdleHiddenGrowthState,
  HoverIdlePrestigeState,
  HoverIdleSettingsState,
  HovTimBalanceSnapshot,
} from "../types";

export const DEFAULT_HOV_TIM_BALANCE_TOTAL = 2;

export const defaultHoverIdleConfig: HoverIdleConfig = {
  hovGainPerSecond: 1,
  timGainPerSecond: 0.01,
  upgradeCost: 1,
  upgradeGainIncrease: 0.1,
  upgradeCostMultiplier: 1.2,
  timUpgradeCost: 10,
  timUpgradeGainIncrease: 0.01,
  timUpgradeCostMultiplier: 1.5,
};

export const defaultHovTimBalance: HovTimBalanceSnapshot = {
  total: DEFAULT_HOV_TIM_BALANCE_TOTAL,
  unassigned: 0,
  hovAllocation: 0,
  timAllocation: DEFAULT_HOV_TIM_BALANCE_TOTAL,
  hovGainAllocation: 0,
  timGainAllocation: 0,
};

export const defaultHiddenGrowth: HoverIdleHiddenGrowthState = {
  hovGainBonus: hugeZero(),
  timGainBonus: hugeZero(),
  hovTimProgress: 0,
};

export const defaultPrestige: HoverIdlePrestigeState = {
  count: 0,
  points: 0,
};

export const defaultSettings: HoverIdleSettingsState = {
  reduceMotion: false,
  lowPowerMode: false,
  language: detectBrowserLocale(),
  achievementNotification: "full",
  wallNotificationMode: "full",
  helpNotificationsEnabled: true,
  readHelpTopicIds: [],
  hovSkillHoldDurationMs: HOV_SKILL_HOLD_MS,
  wallStartChargeDurationMs: DEFAULT_WALL_START_CHARGE_MS,
  notation: "standard",
  firstAccessTutorialCompleted: false,
  mHovAnimationSpeed: "normal",
  mHovChargeDurationMs: 5000,
  nHovAnimationSpeed: "normal",
  nHovChargeDurationMs: 5000,
  oHovChargeDurationMs: 5000,
  outsideResetBurstAnimationEnabled: true,
  hoverToggleDuration: "normal",
  hoverChargeDuration: "normal",
  amplifierUpgradeGuardMs: 0,
  hasSeenAvailableAmplifierUpgradeGuard: false,
  hasSeenMHovButton: false,
  hasSeenNHovButton: false,
};

export const defaultPayment: HoverIdlePaymentState = {
  token: 0,
  redeemedRewardCodes: [],
  purchasedPermanentItems: {
    addMousePointer: 0,
    hovGainPercent: 0,
    timGainPercent: 0,
  },
  entitlements: [],
  claimedEntitlements: [],
};

export const defaultCumulativeStats: HoverIdleCumulativeStatsState = {
  totalHovEarned: hugeZero(),
  totalTimEarned: hugeZero(),
  totalFaiEarned: 0,
  totalTimSpent: hugeZero(),
  lifetimeHovRefinedFromTim: hugeZero(),
  upgradesPurchased: 0,
  hovGainFamilyUpgradesPurchased: 0,
  amplifierUpgradesPurchased: 0,
  prestigeCount: 0,
  onlinePlaytimeSeconds: 0,
  maxHov: hugeZero(),
  hasExecutedNewHovWithoutMergeHov: false,
  hasExecutedMergeHovWithOnlyOneHovTargetCell: false,
  hasExecutedNHovExceedingNotFullyFreeThreshold: false,
  hasExecutedMassiveMergeHov: false,
  hasExecutedOutsideMhov: false,
  hasExecutedOutsideNhov: false,
  redAmplifierFullChargeReached: false,
  totalHovCoreSpent: hugeZero(),
  mhovExecutedByMpCount: 0,
};
